// Sprint 9 — Notificaciones y recordatorios
import { AnimatePresence, motion } from "motion/react";
import { Bell } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { css } from "styled-system/css";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useAuth } from "@/providers/AuthProvider";

type FeedItem = {
  id: string;
  type: string;
  message: string;
  read: boolean;
  createdAt: string;
};

type FeedResponse = {
  unreadTotal: number;
  notifications: FeedItem[];
};

function badgeLabel(n: number): string {
  if (n <= 0) return "";
  if (n > 9) return "9+";
  return String(n);
}

function notifTypeLabel(t: string): string {
  const map: Record<string, string> = {
    BOOKING_NEW: "Nuevo turno",
    BOOKING_CANCELLED: "Cancelación",
    BOOKING_RESCHEDULED: "Cambio de turno",
    REMINDER_24H: "Recordatorio",
    REMINDER_1H: "Recordatorio",
    WHATSAPP_CLIENT_REPLY: "WhatsApp",
    WHATSAPP_UNAVAILABLE: "Sin WhatsApp",
    REFUND_FAILED: "Reembolso pendiente",
  };
  return map[t] ?? t;
}

const PANEL_MAX_W = 352;
const PANEL_GAP = 8;
const VIEWPORT_PAD = 12;

function computePanelPosition(trigger: DOMRect) {
  const w = Math.min(PANEL_MAX_W, window.innerWidth - VIEWPORT_PAD * 2);
  // Preferir abrir a la DERECHA de la campana (sobre el contenido principal), no hacia la izquierda tapando el sidebar.
  let left = trigger.right + PANEL_GAP;
  if (left + w > window.innerWidth - VIEWPORT_PAD) {
    left = trigger.left - w - PANEL_GAP;
  }
  left = Math.max(VIEWPORT_PAD, Math.min(left, window.innerWidth - w - VIEWPORT_PAD));

  const desiredMax = Math.round(window.innerHeight * 0.65);
  const belowTop = trigger.bottom + PANEL_GAP;
  const spaceBelow = window.innerHeight - belowTop - VIEWPORT_PAD;
  const spaceAbove = trigger.top - VIEWPORT_PAD - PANEL_GAP;

  let top: number;
  let maxH: number;
  if (spaceBelow >= Math.min(220, desiredMax) || spaceBelow >= spaceAbove) {
    top = belowTop;
    maxH = Math.max(160, Math.min(spaceBelow, desiredMax));
  } else {
    maxH = Math.max(160, Math.min(spaceAbove, desiredMax));
    top = Math.max(VIEWPORT_PAD, trigger.top - maxH - PANEL_GAP);
  }

  return { top, left, width: w, maxH };
}

export function DashboardNotificationBell() {
  const { refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<FeedResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [layoutTick, setLayoutTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchWithSessionSync("/api/dashboard/notifications", { credentials: "include" }, refresh);
    setLoading(false);
    if (!r.ok) return;
    setData((await r.json()) as FeedResponse);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return;
      const t = e.target as Node;
      if (buttonRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const bump = () => setLayoutTick((t) => t + 1);
    const raf = requestAnimationFrame(bump);
    window.addEventListener("resize", bump);
    window.addEventListener("scroll", bump, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", bump);
      window.removeEventListener("scroll", bump, true);
    };
  }, [open]);

  const panelBox = useMemo(() => {
    if (!open || !buttonRef.current) return null;
    void layoutTick;
    return computePanelPosition(buttonRef.current.getBoundingClientRect());
  }, [open, layoutTick]);

  async function markOne(id: string) {
    const r = await fetchWithSessionSync(
      `/api/dashboard/notifications/${encodeURIComponent(id)}/read`,
      { method: "PATCH", credentials: "include" },
      refresh,
    );
    if (r.ok) void load();
  }

  async function markAll() {
    const r = await fetchWithSessionSync(
      "/api/dashboard/notifications/read-all",
      { method: "PATCH", credentials: "include" },
      refresh,
    );
    if (r.ok) void load();
  }

  const unread = data?.unreadTotal ?? 0;
  const badge = badgeLabel(unread);

  const panelPortal =
    typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence>
            {open && panelBox ? (
              <motion.div
                ref={panelRef}
                key="dash-notif-panel"
                initial={{ opacity: 0, y: -6, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.18 }}
                style={{
                  position: "fixed",
                  top: panelBox.top,
                  left: panelBox.left,
                  width: panelBox.width,
                  maxHeight: panelBox.maxH,
                  zIndex: 200000,
                }}
                className={css({
                  display: "flex",
                  flexDir: "column",
                  rounded: "xl",
                  borderWidth: "1px",
                  borderColor: "rgba(255,255,255,0.12)",
                  bg: "#1e293b",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
                  overflow: "hidden",
                })}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "2",
                    px: "4",
                    py: "3",
                    borderBottomWidth: "1px",
                    borderColor: "rgba(255,255,255,0.08)",
                  })}
                >
                  <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "white" })}>Avisos</p>
                  <button
                    type="button"
                    disabled={unread === 0 || loading}
                    onClick={() => void markAll()}
                    className={css({
                      fontSize: "xs",
                      fontWeight: "semibold",
                      color: "token(colors.brand.300)",
                      bg: "transparent",
                      border: "none",
                      cursor: unread === 0 ? "not-allowed" : "pointer",
                      opacity: unread === 0 ? 0.45 : 1,
                    })}
                  >
                    Marcar todas
                  </button>
                </div>
                <div className={css({ overflowY: "auto", flex: "1", minH: "0", p: "3", boxSizing: "border-box" })}>
                  {loading && !data ? (
                    <p className={css({ fontSize: "sm", color: "rgba(255,255,255,0.55)", p: "3", textAlign: "center" })}>
                      Cargando…
                    </p>
                  ) : !data?.notifications.length ? (
                    <p className={css({ fontSize: "sm", color: "rgba(255,255,255,0.55)", p: "3", textAlign: "center" })}>
                      No tenés avisos sin leer.
                    </p>
                  ) : (
                    <ul className={css({ listStyle: "none", m: "0", p: "0", display: "flex", flexDir: "column", gap: "2" })}>
                      <AnimatePresence initial={false}>
                        {data.notifications.map((n) => (
                          <motion.li
                            key={n.id}
                            layout
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.15 }}
                            className={css({
                              rounded: "lg",
                              bg: "rgba(255,255,255,0.06)",
                              p: "3",
                              borderWidth: "1px",
                              borderColor: "rgba(255,255,255,0.06)",
                              minW: "0",
                              overflow: "hidden",
                            })}
                          >
                            <p className={css({ fontSize: "xs", color: "rgba(255,255,255,0.45)", mb: "1" })}>
                              {notifTypeLabel(n.type)}
                            </p>
                            <p
                              className={css({
                                fontSize: "sm",
                                color: "rgba(255,255,255,0.92)",
                                lineHeight: "1.45",
                                mb: "2",
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                              })}
                            >
                              {n.message}
                            </p>
                            <button
                              type="button"
                              onClick={() => void markOne(n.id)}
                              className={css({
                                fontSize: "xs",
                                fontWeight: "semibold",
                                color: "token(colors.brand.300)",
                                bg: "transparent",
                                border: "none",
                                cursor: "pointer",
                                p: "0",
                              })}
                            >
                              Marcar leída
                            </button>
                          </motion.li>
                        ))}
                      </AnimatePresence>
                    </ul>
                  )}
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body,
        )
      : null;

  return (
    <div className={css({ position: "relative" })}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Notificaciones"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
          if (!open) void load();
        }}
        className={css({
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          w: "10",
          h: "10",
          rounded: "lg",
          border: "none",
          bg: "rgba(255,255,255,0.08)",
          color: "rgba(255,255,255,0.9)",
          cursor: "pointer",
          _hover: { bg: "rgba(255,255,255,0.14)" },
        })}
      >
        <Bell size={20} strokeWidth={2} />
        {badge ? (
          <span
            className={css({
              position: "absolute",
              top: "-2px",
              right: "-2px",
              minW: "18px",
              h: "18px",
              px: "1",
              fontSize: "10px",
              fontWeight: "bold",
              lineHeight: "18px",
              textAlign: "center",
              rounded: "full",
              bg: "#f87171",
              color: "white",
            })}
          >
            {badge}
          </span>
        ) : null}
      </button>

      {panelPortal}
    </div>
  );
}
